import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../shared/providers/LanguageProvider';
import Brand from '../components/common/Brand';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const MerchantResetPassword = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  useEffect(() => {
    const checkRecoveryToken = async () => {
      try {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const type = hashParams.get('type');

        if (type === 'recovery' && accessToken) {
          setTokenValid(true);
        } else {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            setTokenValid(true);
          } else {
            setTokenValid(false);
            setError(t('invalidOrExpiredToken'));
          }
        }
      } catch (err) {
        console.error('Error checking recovery token:', err);
        setTokenValid(false);
        setError(t('invalidOrExpiredToken'));
      }
    };

    checkRecoveryToken();
  }, [t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!password || password.length < 6) {
      setError(t('passwordMinLength'));
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError(t('passwordsDoNotMatch'));
      setLoading(false);
      return;
    }

    try {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const type = hashParams.get('type');

      if (type === 'recovery' && accessToken) {
        const { data, error: updateError } = await supabase.auth.updateUser({
          password: password
        });

        if (updateError) {
          throw updateError;
        }

        setSuccess(true);
        
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: t('passwordResetSuccess'),
              email: data?.user?.email 
            } 
          });
        }, 2000);
      } else {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError || !session) {
          const urlParams = new URLSearchParams(window.location.search);
          const token = urlParams.get('token') || hashParams.get('access_token');
          
          if (token) {
            const { error: exchangeError } = await supabase.auth.verifyOtp({
              token_hash: token,
              type: 'recovery'
            });

            if (exchangeError) {
              throw new Error(t('invalidOrExpiredToken'));
            }

            const { error: updateError } = await supabase.auth.updateUser({
              password: password
            });

            if (updateError) {
              throw updateError;
            }

            setSuccess(true);
            setTimeout(() => {
              navigate('/login', { 
                state: { 
                  message: t('passwordResetSuccess') 
                } 
              });
            }, 2000);
          } else {
            throw new Error(t('invalidOrExpiredToken'));
          }
        } else {
          const { error: updateError } = await supabase.auth.updateUser({
            password: password
          });

          if (updateError) {
            throw updateError;
          }

          setSuccess(true);
          setTimeout(() => {
            navigate('/login', { 
              state: { 
                message: t('passwordResetSuccess') 
              } 
            });
          }, 2000);
        }
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.message || t('passwordResetFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <div className={`flex min-h-screen items-center justify-center bg-background p-4 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
          <p className="text-muted-foreground">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className={`flex min-h-screen items-center justify-center bg-background p-4 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/15">
              <svg className="h-6 w-6 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <CardTitle>{t('invalidToken')}</CardTitle>
            <CardDescription>{error || t('invalidOrExpiredToken')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate('/login')}>
              {t('backToLogin')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className={`flex min-h-screen items-center justify-center bg-background p-4 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15">
              <svg className="h-6 w-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <CardTitle>{t('passwordResetSuccess')}</CardTitle>
            <CardDescription>{t('redirectingToLogin')}</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`relative flex min-h-screen items-center justify-center bg-background p-4 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="absolute end-4 top-4 z-10">
        <LanguageSwitcher />
      </div>

      <Card className="relative w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mb-4 flex justify-center">
            <Brand size="lg" />
          </div>
          <CardTitle className="text-2xl">{t('resetPassword')}</CardTitle>
          <CardDescription>{t('enterNewPassword')}</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-lg border border-destructive/50 bg-destructive/10 p-4">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password">{t('newPassword')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('enterNewPassword')}
                required
                minLength={6}
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground">{t('passwordMinLengthHint')}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('confirmPassword')}
                required
                minLength={6}
                disabled={loading}
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t('resettingPassword') : t('resetPassword')}
            </Button>

            <div className="text-center">
              <Button type="button" variant="link" onClick={() => navigate('/login')}>
                {t('backToLogin')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default MerchantResetPassword;
